import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, FolderOpen, Trash2, ExternalLink } from 'lucide-react';
import { appsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

// Runtime URL for the app
const getRuntimeUrl = (slug: string) => {
  return `https://devfactory-runtime-staging.pages.dev/apps/${slug}`;
};

export function AppsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleOpenApp = (slug: string) => {
    window.open(getRuntimeUrl(slug), '_blank');
  };

  // Fetch apps from API
  const {
    data: appsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Delete app mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => appsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      setDeleteId(null);
    },
  });

  const apps = appsData?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Mes Applications
          </h2>
          <p className="text-sm text-secondary-500">Gérez vos applications no-code</p>
        </div>
        <Button onClick={() => navigate('/apps/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle Application
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-secondary-500">Chargement des applications...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && apps.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300 py-12 dark:border-secondary-700">
          <FolderOpen className="h-12 w-12 text-secondary-400" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900 dark:text-white">
            Aucune application
          </h3>
          <p className="mt-2 text-sm text-secondary-500">
            Créez votre première application pour commencer.
          </p>
          <Button className="mt-4" onClick={() => navigate('/apps/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Créer une application
          </Button>
        </div>
      )}

      {/* Apps grid */}
      {!isLoading && !error && apps.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => (
            <div
              key={app.id}
              className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-secondary-900 dark:text-white">{app.name}</h3>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    app.status === 'published'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : app.status === 'archived'
                        ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}
                >
                  {app.status === 'published' ? 'Publié' : app.status === 'archived' ? 'Archivé' : 'Brouillon'}
                </span>
              </div>
              {app.description && (
                <p className="mt-2 text-sm text-secondary-500 line-clamp-2">{app.description}</p>
              )}
              <div className="mt-4 flex gap-4 text-xs text-secondary-400">
                <span>v{app.version}</span>
                <span>•</span>
                <span>{new Date(app.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  size="sm"
                  onClick={() => navigate(`/apps/${app.id}`)}
                >
                  Modifier
                </Button>
                {app.status === 'published' && app.slug && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenApp(app.slug)}
                    title="Ouvrir l'application"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
                <Dialog open={deleteId === app.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(app.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Supprimer l'application</DialogTitle>
                      <DialogDescription>
                        Êtes-vous sûr de vouloir supprimer "{app.name}" ? Cette action est irréversible.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDeleteId(null)}>
                        Annuler
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => deleteMutation.mutate(app.id)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Supprimer
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
